import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { PublicOrganization } from '@se-po/shared-data-access-models'
import { OrganizationService } from '@se-po/shared-data-access-services'
import { environment } from '@se-po/shared-environments'
import { firstValueFrom } from 'rxjs'

const INBOX_ELEMENT_NAME = 'se-inbox-element'
const INBOX_SCRIPT_NAMES = ['runtime', 'polyfills', 'element']
const LOG = '[messages-new]'

@Component({
  selector: 'app-messages-new-page',
  templateUrl: './messages-new-page.component.html'
})
export class MessagesNewPageComponent implements OnInit {
  private static loadPromise: Promise<void>

  public messagingConfig: object
  public elementReady = false

  constructor(
    private activatedRoute: ActivatedRoute,
    private organizationService: OrganizationService
  ) {}

  public async ngOnInit(): Promise<void> {
    console.log(`${LOG} ngOnInit start. baseUrl=${environment.baseUrl}`)
    try {
      const orgs = await this.userOrgs()
      console.log(`${LOG} fetched orgs (count=${orgs.length}):`, orgs)
      if (orgs.length > 0) {
        this.messagingConfig = this.buildMessagingConfig(orgs)
        console.log(`${LOG} built messagingConfig:`, this.messagingConfig)
      } else {
        console.warn(`${LOG} no orgs returned — messagingConfig will be undefined. Search and send-preview will not work.`)
      }

      await this.ensureElementDefined()
      console.log(`${LOG} custom element defined: ${!!customElements.get(INBOX_ELEMENT_NAME)}`)
      this.elementReady = true

      setTimeout(() => this.inspectMountedElement(), 0)
    } catch (err) {
      console.error(`${LOG} ngOnInit failed:`, err)
    }
  }

  private async userOrgs(): Promise<PublicOrganization[]> {
    const queryOrgId = this.activatedRoute.snapshot.queryParams['orgId']
    if (queryOrgId) {
      console.log(`${LOG} ?orgId=${queryOrgId} — fetching single org via findPublic`)
      try {
        const org = await firstValueFrom(this.organizationService.findPublic(queryOrgId.toString()))
        console.log(`${LOG} findPublic returned:`, org)
        return org ? [org] : []
      } catch (err) {
        console.error(`${LOG} findPublic(${queryOrgId}) failed:`, err)
        return []
      }
    }
    console.log(`${LOG} no orgId query param — calling mine() with no filter`)
    try {
      const response = await firstValueFrom(this.organizationService.mine())
      console.log(`${LOG} mine() response:`, response)
      return response.result ?? []
    } catch (err) {
      console.error(`${LOG} mine() failed:`, err)
      return []
    }
  }

  private buildMessagingConfig(orgs: PublicOrganization[]): object {
    const primary = orgs[0]
    const recipientGroups = orgs.flatMap(org => [
      {
        resourceSystem: 'TeamService',
        resourceType: 'Team',
        params: { org_id: org.id, per_page: 100 }
      },
      {
        resourceSystem: 'UserService',
        resourceType: 'Persona',
        params: { org_id: org.id, exclude_teams: 'true', per_page: 100 }
      },
      {
        resourceSystem: 'Ngin',
        resourceType: 'Group',
        params: { org_id: org.id, per_page: 100, type: 'active' }
      }
    ])
    return {
      orgId: primary.id.toString(),
      orgName: primary.name,
      orgEmail: primary.email,
      recipientGroups
    }
  }

  private async ensureElementDefined(): Promise<void> {
    if (customElements.get(INBOX_ELEMENT_NAME)) {
      console.log(`${LOG} <${INBOX_ELEMENT_NAME}> already registered, skipping script load`)
      return
    }
    console.log(`${LOG} <${INBOX_ELEMENT_NAME}> not registered — loading scripts`)
    MessagesNewPageComponent.loadPromise = MessagesNewPageComponent.loadPromise || this.loadInboxElement()
    await MessagesNewPageComponent.loadPromise
    console.log(`${LOG} all inbox scripts loaded — awaiting customElements.whenDefined`)
    await customElements.whenDefined(INBOX_ELEMENT_NAME)
    console.log(`${LOG} customElements.whenDefined resolved`)
  }

  private async loadInboxElement(): Promise<void> {
    for (const scriptName of INBOX_SCRIPT_NAMES) {
      const url = `${environment.baseUrl}/se-inbox/${scriptName}.js`
      console.log(`${LOG} loading script: ${url}`)
      await this.loadScript(url)
      console.log(`${LOG} loaded script: ${scriptName}.js`)
    }
  }

  private loadScript(src: string): Promise<void> {
    if (document.querySelector(`script[src="${src}"]`)) {
      console.log(`${LOG} script already in DOM, skipping: ${src}`)
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.async = false
      script.src = src
      script.type = 'module'
      script.onload = () => resolve()
      script.onerror = () => {
        console.error(`${LOG} script failed to load: ${src}`)
        reject()
      }
      document.body.appendChild(script)
    })
  }

  private inspectMountedElement(): void {
    const el = document.querySelector(INBOX_ELEMENT_NAME) as HTMLElement & { seConfig?: unknown; messagingConfig?: unknown } | null
    if (!el) {
      console.warn(`${LOG} no <${INBOX_ELEMENT_NAME}> in DOM after elementReady`)
      return
    }
    console.log(`${LOG} mounted element found. tagName=${el.tagName} isConnected=${el.isConnected}`)
    console.log(`${LOG} element.seConfig=`, el.seConfig)
    console.log(`${LOG} element.messagingConfig=`, el.messagingConfig)
    console.log(`${LOG} hasOwnProperty(seConfig)=${Object.prototype.hasOwnProperty.call(el, 'seConfig')}`)
  }
}
